import React, { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { createGSAPContext } from "../hooks/useGSAPCleanup";
import "./Section2CoreIdea.css";

gsap.registerPlugin(ScrollTrigger);

function Section2CoreIdea() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const ctx = createGSAPContext(() => {
      // Set initial states within this section only
      gsap.set(section.querySelectorAll(".core-question"), {
        opacity: 0,
        y: 30,
      });
      gsap.set(section.querySelectorAll(".wrong-answer"), {
        opacity: 0,
        x: -30,
      });
      gsap.set(section.querySelectorAll(".answer-emoji"), {
        opacity: 0,
        scale: 0,
      });
      gsap.set(section.querySelectorAll(".tools-statement"), {
        opacity: 0,
        y: 20,
      });
      gsap.set(section.querySelectorAll(".core-reveal"), {
        opacity: 0,
        scale: 0.9,
      });
      gsap.set(section.querySelectorAll(".final-message"), {
        opacity: 0,
        y: 20,
      });
      gsap.set(section.querySelectorAll(".demo-section"), {
        opacity: 0,
        y: 30,
      });

      // 핵심 메시지를 별도 타임라인으로 빠르게 표시
      const quickReveal = gsap.timeline({
        scrollTrigger: {
          trigger: ".question-section",
          start: "top 75%", // 더 일찍 시작
          end: "bottom 50%",
          toggleActions: "play none none reverse",
        },
      });

      quickReveal
        .to(section.querySelector(".core-question"), {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: "power2.out",
        })
        .to(
          section.querySelectorAll(".wrong-answer"),
          {
            opacity: 1,
            x: 0,
            stagger: 0.15,
            duration: 0.6,
          },
          "-=0.3"
        )
        .to(
          section.querySelectorAll(".answer-emoji"),
          {
            opacity: 1,
            scale: 1,
            stagger: 0.1,
            duration: 0.4,
            ease: "back.out(1.5)",
          },
          "+=0.5"
        )
        .to(
          section.querySelectorAll(".answer-text"),
          {
            opacity: 0.4,
            stagger: 0.1,
            duration: 0.3,
          },
          "<"
        )
        .to(
          section.querySelector(".tools-statement"),
          {
            opacity: 1,
            y: 0,
            duration: 0.6,
          },
          "-=0.2"
        )
        .to(
          section.querySelector(".core-reveal"),
          {
            opacity: 1,
            scale: 1,
            duration: 0.8,
            ease: "back.out(1.2)",
          },
          "-=0.3"
        );

      // Philosophy section animation - enhanced for messages
      const philosophySection = section.querySelector(".philosophy-section");
      if (philosophySection) {
        const philosophyTl = gsap.timeline({
          scrollTrigger: {
            trigger: philosophySection,
            start: "top 75%",
            toggleActions: "play none none reverse",
          },
        });

        philosophyTl
          .fromTo(
            philosophySection,
            {
              opacity: 0,
              y: 50,
            },
            {
              opacity: 1,
              y: 0,
              duration: 1,
              ease: "power2.out",
            }
          )
          .fromTo(
            philosophySection.querySelector(".philosophy-statement"),
            {
              opacity: 0,
              y: 20,
            },
            {
              opacity: 1,
              y: 0,
              duration: 0.6,
              ease: "power2.out",
            },
            "-=0.5"
          )
          .fromTo(
            philosophySection.querySelectorAll(".philosophy-message"),
            {
              opacity: 0,
              x: -30,
            },
            {
              opacity: 1,
              x: 0,
              stagger: 0.2,
              duration: 0.8,
              ease: "power2.out",
            },
            "-=0.2"
          )
          .to(
            philosophySection.querySelector(".philosophy-message.emphasis"),
            {
              scale: 1.05,
              duration: 0.6,
              ease: "back.out(1.5)",
            },
            "-=0.3"
          )
          .fromTo(
            philosophySection.querySelector(".philosophy-insight"),
            {
              opacity: 0,
              y: 30,
              scale: 0.95,
            },
            {
              opacity: 1,
              y: 0,
              scale: 1,
              duration: 1.2,
              ease: "power2.out",
            },
            "<0.5"
          );
      }

      // Card showcase animation
      gsap.set(section.querySelectorAll(".card-item"), {
        opacity: 0,
        y: 50,
        scale: 0.9,
      });

      gsap.to(section.querySelectorAll(".card-item"), {
        scrollTrigger: {
          trigger: ".card-showcase",
          start: "top 75%",
          toggleActions: "play none none reverse",
        },
        opacity: 1,
        y: 0,
        scale: 1,
        stagger: 0.2,
        duration: 0.8,
        ease: "back.out(1.2)",
      });

      // Final message animation
      gsap.from(section.querySelector(".final-message"), {
        scrollTrigger: {
          trigger: ".final-message",
          start: "top 80%",
          toggleActions: "play none none reverse",
        },
        opacity: 0,
        y: 30,
        duration: 1,
        ease: "power2.out",
      });

      // Demo section animation
      const demoSection = section.querySelector(".demo-section");
      if (demoSection) {
        gsap.to(demoSection, {
          scrollTrigger: {
            trigger: demoSection,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power2.out",
        });
      }
    }, section);

    return () => {
      if (ctx && ctx.cleanup) {
        ctx.cleanup();
      }
    };
  }, []);

  return (
    <section className="section section-core-idea" ref={sectionRef}>
      <div className="container">
        <div className="question-section">
          <h2 className="core-question">알리는사람들은 어떤 회사일까요?</h2>
          <div className="answer-options">
            <p className="wrong-answer">
              <span className="answer-text">알리고를 운영하는 회사?</span>
              <span className="answer-emoji">❌</span>
            </p>
            <p className="wrong-answer">
              <span className="answer-text">센드온을 운영하는 회사?</span>
              <span className="answer-emoji">❌</span>
            </p>
            <p className="wrong-answer">
              <span className="answer-text">클라우드 활용 회사?</span>
              <span className="answer-emoji">❌</span>
            </p>
          </div>
          <p className="tools-statement">이것들은 단지 수단일 뿐입니다.</p>
          <div className="core-reveal">
            <h3 className="reveal-title">
              우리의 본질은 <span className="emphasis">고객을 알리는 것</span>
              입니다
            </h3>
          </div>
        </div>

        <div className="philosophy-section">
          <h2 className="philosophy-title">
            그래서 우리는 명함부터 달라야 한다고 생각합니다
          </h2>
          <div className="philosophy-content">
            <p className="philosophy-statement">
              우리를 가리고 고객을 내세움으로써 전하는 메시지
            </p>
            <div className="philosophy-messages">
              <p className="philosophy-message">
                "우리 로고 따위는 중요하지 않습니다"
              </p>
              <p className="philosophy-message">
                "우리는 당신을 알리는 사람들입니다"
              </p>
              <p className="philosophy-message emphasis">
                "당신이 주인공입니다"
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Section2CoreIdea;
